/**
 * Personal files ("My Files") case identity.
 *
 * Deliberately dependency-free: these helpers are read from both ends of the
 * ``cors-edge-lambda-api`` ↔ ``functions`` ↔ ``app-nav-state`` import cycle, so
 * defining them in any of those modules leaves one importer holding an
 * uninitialised binding at module-evaluation time.
 *
 * The UI label is always {@link AD_HOC_CASE_DISPLAY_NAME}. The backend / S3
 * case id is the signed-in user's email (Cognito ``email``, then
 * ``cognito:username``, then ``username``) so each user has their own prefix
 * ``data/bulk/<email>/Manual/``.
 *
 * {@link LEGACY_AD_HOC_CASE_ID} remains for old sessionStorage / URLs / S3
 * objects that still use the shared ``__Ad Hoc`` prefix.
 */

/** Historical shared personal-files case id. */
export const LEGACY_AD_HOC_CASE_ID = '__Ad Hoc';

/**
 * @deprecated Use {@link LEGACY_AD_HOC_CASE_ID} or
 * {@link personalFilesCaseIdFromUsername}. Kept so existing imports compile.
 */
export const AD_HOC_CASE_ID = LEGACY_AD_HOC_CASE_ID;

/** User-facing label for the current user's personal files case. */
export const AD_HOC_CASE_DISPLAY_NAME = 'My Files';

/** Case names that are an email (My Files S3 folder) for Case Management labeling. */
const EMAIL_CASE_NAME_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

let currentPersonalFilesCaseId: string | null = null;

/** Persist a resolved My Files case id. Empty values are ignored so missing claims cannot clobber a prior id. */
export function setCurrentPersonalFilesCaseId(caseId: string | null | undefined): void {
  const trimmed = caseId?.trim() ?? '';
  if (!trimmed) return;
  currentPersonalFilesCaseId = trimmed;
}

export function clearCurrentPersonalFilesCaseId(): void {
  currentPersonalFilesCaseId = null;
}

export function getCurrentPersonalFilesCaseId(): string | null {
  return currentPersonalFilesCaseId;
}

function isPathUnsafeCaseId(value: string): boolean {
  return value.includes('/') || value.includes('\\') || value.includes('..');
}

function stringClaim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Derive the backend case id from the S3 folder name (email, or username fallback). */
export function personalFilesCaseIdFromUsername(
  username: string | null | undefined,
): string | null {
  const trimmed = username?.trim() ?? '';
  if (!trimmed || isPathUnsafeCaseId(trimmed)) return null;
  return trimmed;
}

/**
 * Same derivation as edge-lambda ``personal_files_case_id``.
 * Prefer ``email`` so My Files matches the S3 case folder named from the token.
 */
export function personalFilesCaseIdFromClaims(
  claims: Record<string, unknown> | null | undefined,
): string | null {
  if (!claims) return null;
  return personalFilesCaseIdFromUsername(
    stringClaim(claims.email) ||
      stringClaim(claims['cognito:username']) ||
      stringClaim(claims.username),
  );
}

export function caseIdWithoutTenantPrefix(caseId: string | undefined | null): string {
  if (!caseId) return '';
  if (caseId === '*') return '*';
  const parts = caseId.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : caseId;
}

/** True when the case name is an email-shaped My Files folder. */
export function isEmailCaseName(caseName: string | undefined | null): boolean {
  return EMAIL_CASE_NAME_RE.test(caseIdWithoutTenantPrefix(caseName).trim());
}

/**
 * Case Management list label. Email-named cases are every user's My Files
 * prefix, so show which user they belong to instead of the bare address.
 */
export function getCaseManagementDisplayName(caseName: string | undefined | null): string {
  const internal = caseIdWithoutTenantPrefix(caseName).trim();
  if (!internal) return '';
  if (EMAIL_CASE_NAME_RE.test(internal)) {
    return `'${AD_HOC_CASE_DISPLAY_NAME}' for ${internal}`;
  }
  return internal;
}

/**
 * True when ``caseId`` is the current user's personal files case, or the
 * leftover ``__Ad Hoc`` sentinel.
 */
export function isPersonalFilesCaseId(
  caseId: string | undefined | null,
  personalFilesCaseId?: string | null,
): boolean {
  const internal = caseIdWithoutTenantPrefix(caseId).trim();
  if (!internal) return false;
  // Strip "case:" prefix that's added by personalFilesCaseSummary for the caseTag
  const withoutCasePrefix = internal.startsWith('case:') ? internal.slice(5) : internal;
  const needle = withoutCasePrefix.toLowerCase();
  if (needle === LEGACY_AD_HOC_CASE_ID.toLowerCase()) return true;
  const personal =
    personalFilesCaseIdFromUsername(personalFilesCaseId) ?? getCurrentPersonalFilesCaseId();
  return personal != null && needle === personal.toLowerCase();
}
